import {createSnackbar} from 'Core/ducks/snackbar';
import {AsyncResult, Loaded, Loading} from 'Core/models/Async';

import {
  getPolicyHistory,
  getPolicyRecord,
  getWorkItem,
  lockWorkItem,
  updatePolicyRecord,
  getAssignedWorkItem,
} from 'ApplicationEntry/api/awdApi';
import ApplicationEntryRouteConstants from 'ApplicationEntry/ApplicationEntryRouteConstants';
import {loadPolicyHistory, PolicyHistoryActionType} from 'ApplicationEntry/ducks/policyHistory';
import * as PR from 'ApplicationEntry/ducks/policyRecord';
import {PolicyHistoryItem} from 'ApplicationEntry/models/PolicyHistoryItems';
import {WorkInstanceFieldValue} from 'ApplicationEntry/models/WorkInstanceFieldValue';

import * as E from 'fp-ts/lib/Either';

import {push} from 'connected-react-router';
import {all, call, put, takeEvery, takeLatest} from 'redux-saga/effects';
import {changeAsyncPolicyNumber} from 'ApplicationEntry/ducks/policyRecord';

type WorkSelected = {
  summary: string;
  date: string;
  id: string;
  time: string;
};

type WorkItemChild = {
  workInstance: {
    id: string;
    businessAreaNameContent: string;
    typeNameContent: string;
    statusNameContent: string;
    priorityIncrease: number;
    fieldValues: {
      name: string;
      value: string;
    }[];
  };
};

type workInstance = {
  id: string;
  businessAreaNameContent: string;
  typeNameContent: string;
  statusNameContent: string;
  priorityIncrease: number;
  fieldValues: {
    name: string;
    value: string;
  }[];
};

type PolicyRecordModel = {
  awdKeyId: string;
  priorityIncrease: number;
  fieldValues: {
    name: string;
    value: string;
  }[];
};

type LockWorkItemModel = {
  message: string;
  workInstance: {
    lockedTo: string;
  };
};

function* getPolicyRecordSaga() {
  yield takeEvery(PR.PolicyRecordActionType.GET_POLICY_RECORD, function*() {
    yield put(changeAsyncPolicyNumber(new Loading()));

    let currentWorkResponse: AsyncResult<{
      work: WorkSelected;
      messages: {message: string; responseSeverity: string}[];
    }> = yield call(getAssignedWorkItem); // Attempt to get an assigned work item with the highest priority.

    if (E.isRight(currentWorkResponse)) {
      if (
        currentWorkResponse.right.messages.some(
          responseMessage =>
            responseMessage.message ===
            'There is nothing in the work queue right now. Talk to your supervisor or try again later.'
        )
      ) {
        currentWorkResponse = yield call(getWorkItem);
      }
    }
    if (E.isRight(currentWorkResponse)) {
      const workItemChildrenResponse: AsyncResult<{workInstance: workInstance}> = yield getPolicyRecord(
        currentWorkResponse.right.work.id
      );

      if (E.isRight(workItemChildrenResponse)) {
        const workItemChildren = workItemChildrenResponse.right;

        const coCode = workItemChildren.workInstance.fieldValues.find(x => x.name === WorkInstanceFieldValue.CoCode)
          ?.value;
        const bankNumber = workItemChildren.workInstance.fieldValues.find(
          x => x.name === WorkInstanceFieldValue.BankNumber
        )?.value;
        const SSN = workItemChildren.workInstance.fieldValues.find(x => x.name === WorkInstanceFieldValue.Ssn)?.value;
        let firstName = workItemChildren.workInstance.fieldValues.find(x => x.name === WorkInstanceFieldValue.FirstName)
          ?.value;
        if (firstName?.indexOf(' ') !== -1) {
          firstName = firstName?.substring(0, firstName.indexOf(' ') + 2);
        }
        const lastName = workItemChildren.workInstance.fieldValues.find(x => x.name === WorkInstanceFieldValue.LastName)
          ?.value;
        const policyNumber = workItemChildren.workInstance.fieldValues.find(
          x => x.name === WorkInstanceFieldValue.PolicyNumber
        )?.value;
        const payorNumber = workItemChildren.workInstance.fieldValues.find(
          x => x.name === WorkInstanceFieldValue.PayorNumber
        )?.value;
        const agent = workItemChildren.workInstance.fieldValues.find(x => x.name === WorkInstanceFieldValue.Agent)
          ?.value;
        const stateSigned = workItemChildren.workInstance.fieldValues.find(
          x => x.name === WorkInstanceFieldValue.StateSigned
        )?.value;
        const caseNumber = workItemChildren.workInstance.fieldValues.find(
          x => x.name === WorkInstanceFieldValue.CaseNumber
        )?.value;

        yield put(PR.changeBusinessArea(workItemChildren.workInstance.businessAreaNameContent));
        yield put(PR.changePriorityIncrease(workItemChildren.workInstance.priorityIncrease));
        yield put(PR.changeStatus(workItemChildren.workInstance.statusNameContent));
        yield put(PR.changeWorkType(workItemChildren.workInstance.typeNameContent));
        yield put(PR.changeCoCode(coCode !== undefined ? coCode : ''));
        yield put(PR.changePolicyNumber(policyNumber !== undefined ? policyNumber : ''));
        yield put(PR.changeAsyncPolicyNumber(new Loaded(policyNumber !== undefined ? policyNumber : '')));
        yield put(PR.changeSocialSecurityNumber(SSN !== undefined ? SSN : ''));
        yield put(PR.changeBankNumber(bankNumber !== undefined ? bankNumber : ''));
        yield put(PR.changeFirstName(firstName !== undefined ? firstName : ''));
        yield put(PR.changeLastName(lastName !== undefined ? lastName : ''));
        yield put(PR.changeAgent(agent !== undefined ? agent : ''));
        yield put(PR.changeStateSigned(stateSigned !== undefined ? stateSigned : ''));
        yield put(PR.changePayorNumber(payorNumber !== undefined ? payorNumber : ''));
        yield put(PR.changeCaseNumber(caseNumber !== undefined ? caseNumber : ''));
      } else if (workItemChildrenResponse.left.key === 'Unauthorized') {
        yield put(push(ApplicationEntryRouteConstants.AWDLogin));
      } else {
        yield put(createSnackbar('Unable to retrieve policy record details!'));
      }
    } else if (currentWorkResponse.left.key === 'Unauthorized') {
      yield put(push(ApplicationEntryRouteConstants.AWDLogin));
    } else {
      yield put(createSnackbar('Unable to retrieve policy record details!'));
    }
  });
}

// TO-DO: Unauthorize handle
function* getPolicyHistorySaga() {
  yield takeEvery(PolicyHistoryActionType.GET_POLICY_HISTORY, function*() {
    let currentWorkResponse: AsyncResult<{
      work: WorkSelected;
      messages: {message: string; responseSeverity: string}[];
    }> = yield call(getAssignedWorkItem); // Attempt to get an assigned work item with the highest priority.

    if (E.isRight(currentWorkResponse)) {
      if (
        currentWorkResponse.right.messages.some(
          responseMessage =>
            responseMessage.message ===
            'There is nothing in the work queue right now. Talk to your supervisor or try again later.'
        )
      ) {
        currentWorkResponse = yield call(getWorkItem);
      }
    }

    if (E.isRight(currentWorkResponse)) {
      const policyHistoryItem: AsyncResult<PolicyHistoryItem> = yield getPolicyHistory(
        currentWorkResponse.right.work.id
      );

      if (E.isRight(policyHistoryItem)) {
        yield put(loadPolicyHistory(new Loaded(policyHistoryItem.right.historyEvents)));
      } else if (policyHistoryItem.left.key === 'Unauthorized') {
        yield put(push(ApplicationEntryRouteConstants.AWDLogin));
      } else {
        yield put(loadPolicyHistory(new Loaded([])));
      }
    } else if (currentWorkResponse.left.key === 'Unauthorized') {
      yield put(push(ApplicationEntryRouteConstants.AWDLogin));
    } else {
      yield put(loadPolicyHistory(new Loaded([])));
    }
  });
}

function* updatePolicyRecordApi(action: ReturnType<typeof PR.updatePolicyRecordDetails>) {
  let currentWorkItem: AsyncResult<{
    work: WorkSelected;
    messages: {message: string; responseSeverity: string}[];
  }> = yield call(getAssignedWorkItem); // Attempt to get an assigned work item with the highest priority.

  if (E.isRight(currentWorkItem)) {
    if (
      currentWorkItem.right.messages.some(
        responseMessage =>
          responseMessage.message ===
          'There is nothing in the work queue right now. Talk to your supervisor or try again later.'
      )
    ) {
      currentWorkItem = yield call(getWorkItem);
    }
  }

  if (E.isRight(currentWorkItem)) {
    const lockWorkItemKey: AsyncResult<LockWorkItemModel> = yield lockWorkItem(currentWorkItem.right.work.id);
    const policyRecordDetails: PolicyRecordModel = {
      awdKeyId: currentWorkItem.right.work.id,
      priorityIncrease: action.payload.policyRecordDetails.priorityIncrease,
      fieldValues: [
        {
          name: WorkInstanceFieldValue.FirstName,
          value: action.payload.policyRecordDetails.firstName.value,
        },
        {
          name: WorkInstanceFieldValue.LastName,
          value: action.payload.policyRecordDetails.lastName.value,
        },
        {
          name: WorkInstanceFieldValue.Ssn,
          value: action.payload.policyRecordDetails.socialSecurityNumber.value,
        },
        {
          name: WorkInstanceFieldValue.BankNumber,
          value: action.payload.policyRecordDetails.bankNumber.value,
        },
        {
          name: WorkInstanceFieldValue.PayorNumber,
          value: action.payload.policyRecordDetails.payorNumber.value,
        },
        {
          name: WorkInstanceFieldValue.CaseNumber,
          value: action.payload.policyRecordDetails.caseNumber.value,
        },
        {
          name: WorkInstanceFieldValue.Agent,
          value: action.payload.policyRecordDetails.agent.value,
        },
        {
          name: WorkInstanceFieldValue.PolicyNumber,
          value:
            action.payload.policyRecordDetails.policyNumber.key === 'UserAssignedPolicyNumber'
              ? action.payload.policyRecordDetails.policyNumber.value
              : action.payload.policyRecordDetails.policyNumber.key === 'Loaded'
              ? action.payload.policyRecordDetails.policyNumber.Data
              : '',
        },
        {
          name: WorkInstanceFieldValue.StateSigned,
          value: action.payload.policyRecordDetails.stateSigned.value,
        },
      ],
    } as any;

    if (E.isRight(lockWorkItemKey)) {
      if (lockWorkItemKey.right.workInstance.lockedTo !== '') {
        const response: AsyncResult<string> = yield updatePolicyRecord(policyRecordDetails);

        if (E.isRight(response)) {
          if (response.right === '') {
            yield put(createSnackbar('Updated successfully!'));
            yield put(
              PR.changeAsyncPolicyNumber(
                new Loaded(
                  action.payload.policyRecordDetails.policyNumber.key === 'UserAssignedPolicyNumber'
                    ? action.payload.policyRecordDetails.policyNumber.value
                    : action.payload.policyRecordDetails.policyNumber.key === 'Loaded'
                    ? action.payload.policyRecordDetails.policyNumber.Data
                    : ''
                )
              )
            );
          }
        } else if (response.left.key === 'Unauthorized') {
          yield put(push(ApplicationEntryRouteConstants.AWDLogin));
        } else {
          yield put(createSnackbar('Updated successfully!'));
        }
      } else {
        yield put(createSnackbar('Something went wrong'));
      }
    } else if (lockWorkItemKey.left.key === 'Unauthorized') {
      yield put(push(ApplicationEntryRouteConstants.AWDLogin));
    } else {
      yield put(createSnackbar('Something went wrong'));
    }
  } else if (currentWorkItem.left.key === 'Unauthorized') {
    yield put(push(ApplicationEntryRouteConstants.AWDLogin));
  } else {
    yield put(createSnackbar('Something went wrong'));
  }
}

function* updatePolicyRecordSaga() {
  yield takeLatest(PR.PolicyRecordActionType.UPDATE_POLICY_RECORD, updatePolicyRecordApi);
}

export default function*() {
  yield all([getPolicyRecordSaga(), getPolicyHistorySaga(), updatePolicyRecordSaga()]);
}
